import { useAppRouter } from '@/shared/hooks/useAppRouter'
import { reconcileEventAccess } from '@/shared/lib/auth/access'
import {
  authPaths,
  sanitizeAuthReturnTo,
  toRelativeCallbackPath
} from '@/shared/lib/routing/auth'
import { getCurrentTenantSlug } from '@/shared/lib/tenant'

export const AuthBootstrapShell = () => (
  <div
    style={{
      position: 'fixed',
      inset: 0,
      background: '#fff',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 16,
      padding: 16
    }}
  >
    <div className="flex items-center justify-center">
      <span
        className="inline-block size-8 animate-spin rounded-full border-2 border-primary border-t-transparent"
        role="status"
        aria-label="Carregando"
      />
    </div>
  </div>
)

export function buildRootSignInRedirectUrl({
  callbackUrl,
  error
}: {
  callbackUrl: string
  error?: string
}) {
  return authPaths.signIn({ callbackUrl, error })
}

export function resolveAuthSignInContext(
  router: ReturnType<typeof useAppRouter>,
  digitalProductIdProp?: string
) {
  const queryDigitalProductId = router.query?.digitalProductId
  const queryCallbackUrl = router.query?.callbackUrl
  const callbackUrlRaw =
    Array.isArray(queryCallbackUrl) && queryCallbackUrl.length
      ? queryCallbackUrl[0]
      : queryCallbackUrl

  const eventSlug =
    digitalProductIdProp ||
    (Array.isArray(queryDigitalProductId)
      ? queryDigitalProductId[0]
      : queryDigitalProductId)

  const resolvedEventSlug =
    typeof eventSlug === 'string' ? eventSlug : undefined

  const normalizedCallback = sanitizeAuthReturnTo(
    callbackUrlRaw != null ? String(callbackUrlRaw) : undefined
  )
  const returnTo = sanitizeAuthReturnTo(normalizedCallback)

  return {
    eventSlug: resolvedEventSlug,
    hostTenant: true,
    normalizedCallback,
    returnTo,
    rootSignInRedirectUrl: buildRootSignInRedirectUrl({
      callbackUrl: normalizedCallback,
      error:
        typeof router.query?.error === 'string'
          ? router.query.error
          : Array.isArray(router.query?.error)
            ? router.query.error[0]
            : undefined
    })
  }
}

export function resolveAuthQueryParam(
  value: string | string[] | undefined
): string | undefined {
  if (Array.isArray(value)) return value[0]
  return typeof value === 'string' ? value : undefined
}

export function isNoTicketAuthError(
  error: string | string[] | undefined
): boolean {
  return resolveAuthQueryParam(error) === 'NoTicket'
}

/**
 * Erros de query que a própria tela de auth já trata — não devem abrir o
 * alertdialog genérico "Oops..." do `AuthProvider`.
 */
export function shouldAlertAuthQueryError(
  error: string | string[] | undefined
): boolean {
  const code = resolveAuthQueryParam(error)
  if (!code) return false
  // Tela dedicada `AuthNoTicketView` em `/auth/sign-in?error=NoTicket`.
  if (code === 'NoTicket') return false
  return true
}

/**
 * A tela NoTicket pressupõe participante autenticado sem ingresso.
 * `error=NoTicket` na URL sem sessão é estado órfão — não é essa tela.
 */
export function shouldShowNoTicketView({
  isNoTicketError,
  usableSession
}: {
  isNoTicketError: boolean
  usableSession: boolean
}): boolean {
  return isNoTicketError && usableSession
}

/**
 * Remove `error=NoTicket` quando a sessão já não é utilizável (logout, cookie
 * expirado, URL colada). Sem isso a UI de "falta liberar acesso" aparece sem
 * conta logada e os CTAs de verificar/trocar conta viram no-op.
 */
export function shouldStripStaleNoTicketError({
  isNoTicketError,
  usableSession,
  sessionStatus,
  sessionTimedOut
}: {
  isNoTicketError: boolean
  usableSession: boolean
  sessionStatus: string
  sessionTimedOut: boolean
}): boolean {
  if (!isNoTicketError || usableSession) return false
  if (sessionStatus === 'loading' && !sessionTimedOut) return false
  return true
}

export function shouldShowAuthBootstrapShell({
  isNoTicketError,
  sessionStatus,
  sessionTimedOut,
  usableSession,
  checkingTicket,
  hasAuthContext,
  inNativeAppWebView
}: {
  isNoTicketError: boolean
  sessionStatus: string
  sessionTimedOut: boolean
  usableSession: boolean
  checkingTicket: boolean
  hasAuthContext: boolean
  inNativeAppWebView: boolean
}): boolean {
  if (isNoTicketError) return false

  return (
    (sessionStatus === 'loading' && !sessionTimedOut) ||
    usableSession ||
    checkingTicket ||
    !hasAuthContext ||
    inNativeAppWebView
  )
}

export async function redirectToNoTicket(
  router: ReturnType<typeof useAppRouter>,
  callbackUrl?: string
) {
  router.replace(authPaths.signIn({ error: 'NoTicket', callbackUrl }))
}

const INVITE_RETURN_PREFIX = '/convites/'

/**
 * `returnTo` aponta para a tela de convite explícito (`/convites/{code}`).
 *
 * Usado por `completePostAuthNavigation` para não deixar a reconciliação de
 * acesso (que concede o convite default) rodar nessa janela — o convite
 * específico do link/QR precisa ser o que fica de fato vinculado à conta, não
 * o default entry. `Assign.jsx` resolve o aceite explícito por conta própria
 * assim que a sessão fica usável, sem depender dessa reconciliação.
 */
export function isInviteReturnTo(returnTo: string): boolean {
  return returnTo.startsWith(INVITE_RETURN_PREFIX)
}

export async function completePostAuthNavigation({
  router,
  returnTo,
  accessToken,
  eventSlug
}: {
  router: ReturnType<typeof useAppRouter>
  returnTo: string
  accessToken?: string
  eventSlug?: string
}): Promise<'allowed' | 'no-ticket' | 'unknown' | 'invite'> {
  if (isInviteReturnTo(returnTo)) {
    window.location.replace(returnTo)
    return 'invite'
  }

  // user_access no backend tenta ingresso existente e, se faltar, o default
  // entry (`EnsureDefaultEntryService`) — é o trilho correto de "verificar
  // acesso novamente".
  const reconciliation = await reconcileEventAccess({
    tenantSlug: eventSlug || getCurrentTenantSlug(),
    accessToken
  })

  if (reconciliation === 'no-ticket') {
    await redirectToNoTicket(router, returnTo)
    return 'no-ticket'
  }

  if (reconciliation === 'allowed') {
    window.location.replace(returnTo)
    return 'allowed'
  }

  // unknown: sem token/slug ou falha de rede — não empurrar o participante
  // para a área autenticada sem prova de acesso.
  return 'unknown'
}
